import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Alert,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";
import { AuthCard } from "@components/AuthCard";
import { authV2Selector, authV2InvitationPassword, AUTH_REQUEST_KEYS } from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { AuthPasswordStrength } from "@/components/AuthPasswordStrength";

const AuthInviteSetPassword = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const authV2 = useSelector(authV2Selector);
  const invitationStatus = authV2?.invitationStatus;
  const invitationEmail = invitationStatus?.invitation?.email;
  const loading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.invitationPassword]);
  const error = authV2?.errors?.[AUTH_REQUEST_KEYS.invitationPassword];
  const isExistingAccountError = error?.code === "ACCOUNT_EXISTS_USE_LOGIN_OR_RESET";

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  useEffect(() => {
    if (!isExistingAccountError) return undefined;

    const signInParams = new URLSearchParams();
    if (invitationEmail) signInParams.set("email", invitationEmail);
    signInParams.set("invitationToken", token);
    const signInQuery = signInParams.toString();
    const signInPath = signInQuery ? `/auth/sign-in?${signInQuery}` : "/auth/sign-in";
    const redirectTimeoutId = window.setTimeout(() => navigate(signInPath), 6000);

    return () => window.clearTimeout(redirectTimeoutId);
  }, [isExistingAccountError, invitationEmail, navigate, token]);

  // If error, display error snackbar.
  useEffect(() => {
    if (isExistingAccountError) return;

    if (error?.message) {
      dispatch(
        openSnackbar({
          message: error.message,
          intent: "danger",
          description: error.errorDescriptions,
        }),
      );
    }
  }, [error, isExistingAccountError, dispatch]);

  const formik = useFormik({
    initialValues: {
      password: "",
    },
    validationSchema: yup.object({
      password: yup
        .string()
        .required("Password is required.")
        .min(14, "Please use a password with at least 14 characters.")
        .matches(/\d/, "Password must contain at least 1 number.")
        .matches(/[^a-zA-Z0-9]/, "Password must contain at least 1 special character."),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      const payload = {
        ...values,
        token,
      };
      dispatch(authV2InvitationPassword(payload));
    },
  });
  return (
    <AuthCard onSubmit={() => formik.handleSubmit()}>
      {isExistingAccountError ? (
        <Alert
          intent="neutral"
          variant="filled"
          description="We found an existing user for this email address. We're redirecting you to sign in now."
        />
      ) : (
        <Stack rowGap={6}>
          <Stack rowGap={3}>
            <Stack flexDirection="column">
              <Typography level="body-lg">Organisation</Typography>
              <Typography level="body-lg" bold>
                {invitationStatus?.invitation?.organisationName}
              </Typography>
            </Stack>
            <Stack flexDirection="column">
              <Typography level="body-lg">Email</Typography>
              <Typography level="body-lg" bold>
                {invitationEmail}
              </Typography>
            </Stack>
            <FormControl error={!!formik.errors.password}>
              <FormLabel size="md">Password</FormLabel>
              <Input
                autoFocus
                placeholder="Your password"
                type="password"
                value={formik.values.password}
                onChange={(event) => {
                  formik.setFieldValue("password", event.target.value);
                  if (formik.errors.password) {
                    formik.setFieldError("password", undefined);
                  }
                }}
                size="lg"
              />
              {formik.errors.password ? (
                <FormHelperText>
                  <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                  {formik.errors.password}
                </FormHelperText>
              ) : null}
            </FormControl>
          </Stack>
          <AuthPasswordStrength password={formik.values.password} />
          <Button
            size="lg"
            variant="primary"
            endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
            type="submit"
            loading={loading}
          >
            Continue
          </Button>
        </Stack>
      )}
    </AuthCard>
  );
};

export { AuthInviteSetPassword };
